import { Link } from 'react-router-dom';
import { SEO } from '@/components/SEO';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  UserCheck, Users, Clock, FileCheck, TrendingUp, Briefcase,
  GraduationCap, Bot, BarChart3, ArrowRight, Shield, Zap, Globe
} from 'lucide-react';

export function ProductOverviewPage() {
  const modules = [
    {
      icon: UserCheck,
      name: 'Core HR',
      description: 'Complete employee lifecycle management with digital employee records, org charts, and more.',
      href: '/product/core-hr',
      features: ['Employee Database', 'Org Hierarchy', 'Document Management', 'Custom Fields']
    },
    {
      icon: Users,
      name: 'Onboarding & Offboarding',
      description: 'Seamless new hire experience with automated workflows and digital document collection.',
      href: '/product/onboarding',
      features: ['Pre-boarding', 'Digital Forms', 'Task Management', 'Exit Workflows']
    },
    {
      icon: Clock,
      name: 'Attendance & Leave',
      description: 'Geo-fencing, mobile punch, biometric integration, and automated leave management.',
      href: '/product/attendance-leave',
      features: ['Geo-fencing', 'Mobile Punch', 'Leave Policies', 'Regularization']
    },
    {
      icon: FileCheck,
      name: 'Payroll & Compliance',
      description: 'Automated payroll processing with statutory compliance for multiple countries.',
      href: '/product/payroll',
      features: ['Auto Payroll', 'Tax Calculations', 'Statutory Forms', 'Multi-currency']
    },
    {
      icon: TrendingUp,
      name: 'Performance Management',
      description: 'OKRs, 9-Box grids, Bell Curve assessments, and continuous feedback systems.',
      href: '/product/performance',
      features: ['OKRs', '9-Box Grid', 'Bell Curve', '360° Feedback']
    },
    {
      icon: Briefcase,
      name: 'Talent Management',
      description: 'Succession planning, career pathing, and skills management for future-ready teams.',
      href: '/product/talent-management',
      features: ['Succession Planning', 'Career Paths', 'Skills Matrix', 'Talent Pools']
    },
    {
      icon: Users,
      name: 'Recruitment / ATS',
      description: 'End-to-end hiring workflow from requisition to offer letter with candidate portal.',
      href: '/product/recruitment',
      features: ['Job Posting', 'Applicant Tracking', 'Interview Scheduling', 'Offer Management']
    },
    {
      icon: GraduationCap,
      name: 'Learning & Development',
      description: 'Training programs, certifications, and learning paths for continuous employee growth.',
      href: '/product/learning',
      features: ['Course Management', 'Certifications', 'Learning Paths', 'Skills Development']
    },
    {
      icon: Bot,
      name: 'AI HR Assistant',
      description: 'Intelligent chatbot for employee queries, policy information, and self-service.',
      href: '/product/ai-assistant',
      features: ['24/7 Support', 'Policy Queries', 'Leave Status', 'Smart Recommendations']
    },
    {
      icon: BarChart3,
      name: 'Analytics & Dashboards',
      description: 'Real-time insights with customizable dashboards and predictive analytics.',
      href: '/product/analytics',
      features: ['Real-time Dashboards', 'Custom Reports', 'Predictive Analytics', 'Export Options']
    },
  ];

  const platformBenefits = [
    {
      icon: Zap,
      title: 'Lightning Fast',
      description: 'Modern architecture ensures instant loading and seamless performance'
    },
    {
      icon: Shield,
      title: 'Enterprise Security',
      description: 'SOC 2, ISO 27001 certified with bank-grade encryption'
    },
    {
      icon: Globe,
      title: 'Global Ready',
      description: 'Multi-country, multi-currency, multi-language support out of the box'
    }
  ];

  return (
    <>
      <SEO
        title="Product Overview - Nestor"
        description="Explore Nestor with modules for Core HR, Payroll, Performance, Talent Management, and more. Everything you need from hire to retire."
      />

      {/* Hero */}
      <section className="bg-gradient-to-b from-primary/5 via-background to-background py-24">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="flex-1 text-center lg:text-left">
              <h1 className="text-4xl md:text-6xl font-bold mb-6 tracking-tight">
                Complete Nestor Platform
              </h1>
              <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
                Everything you need to manage your workforce from hire to retire. One platform, infinite possibilities.
                Designed to be <span className="text-primary font-semibold">easy to use</span> for everyone.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Button size="lg" className="h-12 px-8 rounded-full" asChild>
                  <Link to="/demo">
                    Request Demo <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button size="lg" variant="outline" className="h-12 px-8 rounded-full" asChild>
                  <Link to="/pricing">View Pricing</Link>
                </Button>
              </div>
            </div>

            <div className="flex-1 w-full max-w-lg lg:max-w-xl">
              <div className="aspect-square bg-gradient-to-tr from-blue-100 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-3xl flex items-center justify-center shadow-sm border border-border/50 overflow-hidden">
                <img
                  src="/images/illustrations/hero-team.png"
                  alt="HR Team Collaboration"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Platform Benefits */}
      <section className="py-16 border-b">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {platformBenefits.map((benefit, index) => (
              <div key={index} className="flex flex-col items-center text-center">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                  <benefit.icon className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-semibold mb-2">{benefit.title}</h3>
                <p className="text-sm text-muted-foreground">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Modules Grid */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Powerful Modules, Unified Experience
            </h2>
            <p className="text-lg text-muted-foreground">
              Each module is designed to work seamlessly together while being powerful enough to stand alone.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
            {modules.map((module, index) => (
              <Link key={index} to={module.href}>
                <Card className="h-full hover:shadow-lg transition-all hover:-translate-y-1">
                  <CardHeader>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <module.icon className="h-5 w-5 text-primary" />
                      </div>
                      <CardTitle className="text-lg">{module.name}</CardTitle>
                    </div>
                    <CardDescription>{module.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {module.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm">
                          <div className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5" />
                          <span className="text-muted-foreground">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-muted/50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">
              Ready to See It in Action?
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Schedule a personalized demo to see how our platform can transform your HR operations.
            </p>
            <Button size="lg" asChild>
              <Link to="/demo">
                Request Demo <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </>
  );
}
